import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import { options, sortOptions, getTagLabel } from "./options";
import { useTagSearch } from "../../hooks/useTagSearch";

import { ILLUSTRATIONS } from "./Ilustrations";
import Select from "react-select";

import PageTitle from "../../components/PageTitle";
//import { WaveCanvas } from '../../components/WaveCanvas';
import "./Gallery.css";



const ITEMS_PER_PAGE_PC = 15;
const ITEMS_PER_PAGE_MOBILE = 16;




//いいね機能用
const LIKE_API_URL = `/likes.php?t=${Date.now()}`;





const GalleryCard = React.memo(({ illust, location, isScrolling }: { illust: typeof ILLUSTRATIONS[number]; location: any; isScrolling?: boolean; }) => {
	//ギャラリーの画像(ボタン)をここで表示させる。

	const [isLoaded, setIsLoaded] = useState(false);

	const displayTitle = illust.title?.trim() || "無題";	//タイトルが無入力の場合は無題、何かしら値が入ってたら引っ張ってくる
	const tooltipText = `${displayTitle}\n投稿日: ${illust.date}`;	//で、実際に表示させるモノ

	const handleTouch = () => {
		// 振動をサポートしているブラウザか確認して実行
		if (window.navigator.vibrate) {
			window.navigator.vibrate(15); // 15msくらいがちょうどいい「クリック感」
		}
	};

	// 複数画像対応: srcプロパティが配列なら1枚目を、文字列ならそのまま使用。
	// srcがなければ images 配列の1枚目を使用する
	const displaySrc = Array.isArray(illust.src) ? illust.src[0] : (illust.src || (illust as any).images?.[0] || "");


	return (
		<Link
			to={`/illust/${illust.id}`}
			state={{ backgroundLocation: location }}
			onTouchStart={handleTouch} //触ったらちょっとスマホを震わせる
			style={{
				// 読み込み中、またはスクロール中(isScrollingがtrue)の時はクリックを無効にする
				cursor: (isLoaded && !isScrolling) ? 'pointer' : 'default',
				display: 'block',
				position: 'relative',
				width: '100%',
				overflow: 'hidden',
				aspectRatio: '1 / 1'
			}}
		>
			{/* 読み込み中だけ表示されるプレースホルダー */}
			{!isLoaded && (
				<div className="loader-container">
					<div className="spinner"></div>
				</div>
			)}

			<img
				src={displaySrc}
				alt={illust.title}
				className={`gallery-img ${isLoaded ? 'loaded' : ''}`}
				loading="lazy"
				decoding="async"
				title={tooltipText}
				onLoad={() => setIsLoaded(true)}
				style={{
					width: '100%',
					height: '100%',
					objectFit: 'cover',
					pointerEvents: (isLoaded && !isScrolling) ? 'auto' : 'none',
					opacity: isLoaded ? 1 : 0,
				}}
			/>
		</Link>
	);
});






const Gallery = () => {
	const location = useLocation(); // 今の場所を記録

	// 触覚フィードバック
	type VibePattern = number | number[];

	const triggerHaptic = (pattern: VibePattern) => {
		if (typeof window !== "undefined" && window.navigator && window.navigator.vibrate) {
			window.navigator.vibrate(pattern as any);
		}
	};

	//検索機能を宣言
	const [searchParams] = useSearchParams();

	// URLから一時的に値を抽出
	const tQuery = searchParams.get('q') || '';
	const tYearParam = searchParams.get('y');
	const tYear = tYearParam === "All" || !tYearParam ? "All" : Number(tYearParam);

	// baseFilteredList を先に作成
	const baseFilteredList = React.useMemo(() => {
		return ILLUSTRATIONS.filter(illust => {
			const query = tQuery.toLowerCase();
			const tagLabels = illust.tags.map((tagId: string) => getTagLabel(tagId));
			const matchSearch = tQuery === "" || illust.title.toLowerCase().includes(query) || illust.comment.toLowerCase().includes(query) || tagLabels.some((label: string) => label.toLowerCase().includes(query));
			const illustYear = new Date(illust.date).getFullYear();
			const matchYear = tYear === "All" || illustYear === tYear;
			return matchSearch && matchYear;
		});
	}, [tQuery, tYear]);

	// フックを呼び出す (ここで searchQuery や currentTags が定義される)
	const { displayTags, handleChange, searchQuery, handleSearchChange, optionsWithCounts, currentTags, selectedYear, handleYearChange } = useTagSearch(options, baseFilteredList);

	// フックで定義された変数を「初期値」に使う state を、ここで宣言する
	const [currentPage, setCurrentPage] = useState(1);
	const [inputValue, setInputValue] = useState(searchQuery); // エラー解消！
	const [sortType, setSortType] = useState("newest");
	const [likesMap, setLikesMap] = useState<{ [key: string]: number }>({});


	const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_PC);
	const [myLikedIds, setMyLikedIds] = useState<string[]>([]);
	const [isScrolling, setIsScrolling] = useState(false);


	useEffect(() => {
		// デバッグ用：これがコンソールに出れば、リセット処理が「発火」しています
		console.log("検索条件が変わったので1ページ目に戻します");

		setCurrentPage(1);

		// 監視対象に location.search (URLの?以降) を追加
	}, [location.search, searchQuery, selectedYear]);


	// 今日のイラストを決定（ILLUSTRATIONSの中から1枚）
	const dailyPick = React.useMemo(() => {
		const picks = [];
		const total = ILLUSTRATIONS.length;
		// 日付から計算
		const today = new Date();
		const dateNum = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();

		for (let i = 0; i < 3; i++) {
			// i を足すことで、1枚目、2枚目、3枚目で違うインデックスを選ぶ
			const index = (Math.floor((dateNum * (9301 + i) + 49297) % 233280) / 233280 * total);
			const item = ILLUSTRATIONS[Math.floor(index)];
			if (item) {
				picks.push(item);
			}
		}
		return picks;
	}, []);



	// リサイズ検知
	useEffect(() => {
		const handleResize = () => {
			const newItemsPerPage = window.innerWidth <= 768 ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_PC;
			if (newItemsPerPage !== itemsPerPage) {
				setItemsPerPage(newItemsPerPage);
				setCurrentPage(1); // ページ数が変わるので1ページ目に戻す
			}
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [itemsPerPage]);


	// URLのsearchQueryが変わったら入力欄も同期
	React.useEffect(() => {
		setInputValue(searchQuery);
	}, [searchQuery]);

	const availableYears = Array.from(
		new Set(ILLUSTRATIONS.map(illust => new Date(illust.date).getFullYear()))
	).sort((a, b) => b - a);



	// 検索実行用の関数（useEffectの外で定義）
	const executeSearch = React.useCallback((value: string) => {
		const event = { target: { value } } as React.ChangeEvent<HTMLInputElement>;
		handleSearchChange(event);
		setCurrentPage(1);
	}, [handleSearchChange]); // handleSearchChangeを依存配列に含める



	// デバウンス: 入力が止まってから200ms後に検索実行
	React.useEffect(() => {
		const timer = setTimeout(() => {
			if (inputValue !== searchQuery) {
				executeSearch(inputValue);
			}
		}, 200);

		return () => clearTimeout(timer);
	}, [inputValue, searchQuery, executeSearch]);



	//入力されたときの処理
	const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
	};



	//Enterを押すと検索する
	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			executeSearch(inputValue);
		}
	};


	//イラストの絞り込み処理(タグ)

	const filteredIllusts = React.useMemo(() => ILLUSTRATIONS.filter(illust => {

		//  検索キーワードの準備
		const query = searchQuery.toLowerCase();

		//  タグIDを日本語名(ラベル)に変換
		const tagLabels = illust.tags.map((tagId: string) => getTagLabel(tagId));

		//  【検索条件】タイトル or コメント or タグ名 にキーワードが含まれるか
		const matchSearch =
			searchQuery === "" ||
			illust.title.toLowerCase().includes(query) ||
			illust.comment.toLowerCase().includes(query) ||
			tagLabels.some((label: string) => label.toLowerCase().includes(query));

		// 【タグ選択（セレクトボックス）】の条件
		const matchTags =
			currentTags.length === 0 ||
			currentTags.every(tag => illust.tags.includes(tag));

		// 【年選択】の条件
		const illustYear = new Date(illust.date).getFullYear();
		const matchYear = selectedYear === "All" || illustYear === selectedYear;

		// 【お気に入り】選択時の条件
		const matchMyLikes = sortType === 'my-likes' ? myLikedIds.includes(String(illust.id)) : true;

		// すべての条件を満たしているものだけを返す
		return matchSearch && matchTags && matchYear && matchMyLikes && (likesMap || true) && (sortType || true);
	}), [searchQuery, currentTags, selectedYear, sortType, myLikedIds, likesMap]);

	console.log("選択中のタグ:", currentTags, "結果の件数:", filteredIllusts.length);



	// 絞り込み結果をソート
	const sortedIllusts = React.useMemo(() => {
		const base = [...filteredIllusts];

		return base.sort((a, b) => {
			if (sortType === 'my-likes') {
				return myLikedIds.indexOf(String(b.id)) - myLikedIds.indexOf(String(a.id));
			}

			if (sortType === 'popular') {
				// IDを文字列に変換してlikesMapから取得
				const countA = likesMap[String(a.id)] || 0;
				const countB = likesMap[String(b.id)] || 0;
				return countB - countA;
			}

			// 名前順 (昇順)
			if (sortType === 'titleAsc') {
				return (a.title || "無題").localeCompare(b.title || "無題", 'ja');
			}

			// ★名前順 (降順)
			if (sortType === 'titleDesc') {
				return (b.title || "無題").localeCompare(a.title || "無題", 'ja');
			}

			const timeA = new Date(a.date).getTime();
			const timeB = new Date(b.date).getTime();
			return sortType === 'oldest' ? timeA - timeB : timeB - timeA;
		});
	}, [filteredIllusts, sortType, likesMap, myLikedIds]);




	//ページネーション用の計算
	const maxPage = Math.ceil(sortedIllusts.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const currentDisplayList = sortedIllusts.slice(startIndex, startIndex + itemsPerPage);






	// DBから現在のいいね数を取得する関数
	const fetchLikes = async () => {
		try {
			const res = await fetch(LIKE_API_URL);
			if (!res.ok) throw new Error('Network error');
			const data = await res.json();

			// 配列 [{illust_id: "kar1", count: "5"}, ...] を 
			// オブジェクト { kar1: 5, ... } に変換して扱いやすくする
			const map = data.reduce((acc: any, item: any) => {
				acc[item.illust_id] = parseInt(item.count);
				return acc;
			}, {});
			setLikesMap(map);

		} catch (err) {
			console.error("いいね取得失敗:", err);
		}
	};

	// ページ読み込み時に実行
	useEffect(() => {
		const saved = localStorage.getItem('my_likes');
		let likedList: string[] = saved ? JSON.parse(saved) : [];
		let migrationOccurred = false;

		ILLUSTRATIONS.forEach(illust => {
			const oldKey = `liked_${illust.id}`;
			if (localStorage.getItem(oldKey) === "true" && !likedList.includes(String(illust.id))) {
				likedList.push(String(illust.id));
				migrationOccurred = true;
			}
		});

		if (migrationOccurred) {
			localStorage.setItem('my_likes', JSON.stringify(likedList));
		}

		setMyLikedIds(likedList); // stateを更新
		fetchLikes();
	}, []);

	// ページ読み込み時に実行
	useEffect(() => {
		fetchLikes();

	}, []);


	// スクロール開始を検知したらアニメーションを抑制する
	useEffect(() => {
		let scrollTimeout: any;

		const handleScroll = () => {
			setIsScrolling(true); // スクロール中フラグをON
			clearTimeout(scrollTimeout);
			scrollTimeout = setTimeout(() => {
				setIsScrolling(false); // 止まったらフラグをOFF
			}, 150);
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => {
			window.removeEventListener('scroll', handleScroll);
			clearTimeout(scrollTimeout);
		};
	}, []);


	// ソート順が変更されたら、強制的に1ページ目に戻す
	useEffect(() => {
		setCurrentPage(1);
	}, [sortType]);



	return (

		<div>
			<div className="profile-page-wrapper"></div>
			{/*<WaveCanvas />*/}

			<h1 className="top-title">Gallery</h1>

			<div className="Searchflame">
				{/* 検索欄メニュー */}


				{/* 年選択プルダウン */}
				<select className='QuickYearMenu'
					value={selectedYear}
					onChange={(e) => {
						const val = e.target.value === "All" ? "All" : Number(e.target.value);
						handleYearChange(val);
						setCurrentPage(1);
					}}

				>
					<option value="All">すべての年</option>
					{availableYears.map(year => (
						<option key={year} value={year}>{year}年</option>
					))}


				</select>

				<select className='SortMenu' value={sortType} onChange={(e) => setSortType(e.target.value)}>
					{sortOptions.map(opt => (
						<option key={opt.value} value={opt.value}>{opt.label}</option>
					))}
				</select>


				<input className="QuickSearchMenu"
					type="text"
					value={inputValue}
					onChange={onInputChange}
					onKeyDown={onKeyDown}
					placeholder="キーワード検索..."
				/>


				{/* タグ検索 */}
				<div className="TagSearchMenu" style={{ width: '100%', boxSizing: 'border-box' }}>
					<Select isMulti
						options={optionsWithCounts}
						value={displayTags}
						onChange={(val) => {
							handleChange(val);
							setCurrentPage(1);
						}}

						placeholder="ソート / フィルタ"


						styles={{
							control: (base) => ({
								...base,
								width: '100%',
								boxSizing: 'border-box',
								minHeight: '40px', //検索欄の高さ
								fontSize: '18px',  //文字を大きさ
								borderRadius: '10px', //角の丸さ
							}),
							placeholder: (base) => ({
								...base,
								color: '#888',

							})
						}}
						menuPortalTarget={document.body}
					/>
				</div>





			</div>

			<br />

			{/* 今日のイラストピックアップ */}
			<AnimatePresence>
				{displayTags.length === 0 && searchQuery === "" && (
					<motion.section
						key="daily-pickup" // アニメーションの識別に必要
						className="daily-pick-flame"
						initial={{ opacity: 0, y: -10 }} // 最初は少し上で透明
						animate={{ opacity: 1, y: 0 }}   // ふわっと定位置に
						exit={{ opacity: 0, y: 10 }}    // 消える時も少し上に浮きながら透明に
						transition={{ duration: 0.3 }}    // 0.3秒かけて動く（下のタイルと同じ感覚）
					>
						<span className="daily-pick-title">Today's Pick up</span>

						<div className={`daily-pick-container ${isScrolling ? 'scrolling' : ''}`}>
							{dailyPick.map((illust, index) => (
								<div key={`${illust.id}-${index}`} className="daily-pick-item">
									{/* isScrolling を渡して、スクロール中のクリックを無効化する */}
									<GalleryCard
										illust={illust}
										location={location}
										isScrolling={isScrolling}
									/>
								</div>
							))}
						</div>

						<small style={{ color: '#ffffff', fontSize: '0.7rem', marginTop: '10px' }}>
							毎日0時に更新
						</small>
					</motion.section>
				)}
			</AnimatePresence>


			{/* ここからギャラリーページ */}

			<div className='gallery'>

				{currentDisplayList.length > 0 && (
					<div className="pagination-controls" style={{ marginTop: '40px', textAlign: 'center' }}>

						{/* 一番最初のページ */}
						<button
							onClick={() => {
								setCurrentPage(1);
								triggerHaptic([10, 30, 10]);
							}}
							disabled={currentPage === 1}
						>
							＜＜
						</button>


						{/* 前へ */}
						<button
							onClick={() => {
								setCurrentPage(p => Math.max(1, p - 1));
								triggerHaptic(10);
							}}

							disabled={currentPage === 1}
						>
							＜
						</button>


						{/* 現在のページ */}
						<span style={{
							margin: '0 20px',
							fontFamily: '"Zen Maru Gothic", sans-serif',
							fontSize: '18px',
							color: 'white'
						}}>{currentPage} / {maxPage}</span>


						{/* 次へ */}
						<button
							onClick={() => {
								setCurrentPage(p => Math.min(maxPage, p + 1));
								triggerHaptic(10);
							}}
							disabled={currentPage === maxPage || maxPage === 0}
						>
							＞
						</button>


						{/* 最後のページへ */}
						<button
							onClick={() => {
								setCurrentPage(maxPage);
								triggerHaptic([10, 30, 10]);

							}}
							disabled={currentPage === maxPage || maxPage === 0}
						>
							＞＞
						</button>
					</div>
				)}


				{/* 検索・フィルタ・ソートが初期状態の時のみ、総登録数を表示 */}
				{searchQuery === "" && currentTags.length === 0 && selectedYear === "All" && sortType === "newest" && (
					<div style={{ textAlign: 'center', color: '#ccc', fontSize: '0.9rem', marginTop: '10px', marginBottom: '10px', fontFamily: '"Zen Maru Gothic", sans-serif' }}>
						現在 {ILLUSTRATIONS.length} 点の作品が登録済み。
					</div>
				)}


				<div className={`grid ${isScrolling ? 'scrolling' : ''}`} style={{ position: 'relative' }}>

					<AnimatePresence mode="wait" initial={true}>
						{currentDisplayList.length > 0 ? (
							// モーション付きのコンテナ


							currentDisplayList.map((illust, index) => (
								<motion.div
									key={`${illust.id}-${currentPage}`}

									initial={{ opacity: 0 }} // 少し下から浮き上がる演出を足すと変化が分かりやすい
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}

									transition={{
										duration: 0.2,
										delay: (index % 12) * 0.02
									}}

								>

									<GalleryCard
										illust={illust}
										location={location}
										isScrolling={isScrolling}
									/>

								</motion.div>
							))
						) : (
							<motion.div
								key="not-found"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								className='NotApplicable'
							>
								該当するものがありません…<br />
								検索条件を変えてみて下さい。
							</motion.div>
						)}
					</AnimatePresence>
				</div>




				{/* ページネーション操作パネル */}
				{currentDisplayList.length > 0 && (
					<div className="pagination-controls" style={{ marginTop: '40px', textAlign: 'center' }}>

						{/* 一番最初のページ */}
						<button
							onClick={() => {
								setCurrentPage(1);
								triggerHaptic([10, 30, 10]);
							}}
							disabled={currentPage === 1}
						>
							＜＜
						</button>


						{/* 前へ */}
						<button
							onClick={() => {
								setCurrentPage(p => Math.max(1, p - 1));
								triggerHaptic(10);
							}}

							disabled={currentPage === 1}
						>
							＜
						</button>


						{/* 現在のページ */}
						<span style={{
							margin: '0 20px',
							fontFamily: '"Zen Maru Gothic", sans-serif',
							fontSize: '18px',
							color: 'white'
						}}>{currentPage} / {maxPage}</span>


						{/* 次へ */}
						<button
							onClick={() => {
								setCurrentPage(p => Math.min(maxPage, p + 1));
								triggerHaptic(10);
							}}
							disabled={currentPage === maxPage || maxPage === 0}
						>
							＞
						</button>


						{/* 最後のページへ */}
						<button
							onClick={() => {
								setCurrentPage(maxPage);
								triggerHaptic([10, 30, 10]);

							}}
							disabled={currentPage === maxPage || maxPage === 0}
						>
							＞＞
						</button>
					</div>
				)}
			</div>



			<PageTitle title={"Gallery"} />


		</div>

	);
};

export default Gallery;